import { BeneficiaryAccountStep } from './BeneficiaryAccountStep';
import { BillPaymentStep } from './BillPaymentStep';
import { BorrowerStep } from './BorrowerStep';
import { CustodianStep } from './CustodianStep';
import { CustomCcbStep } from './CustomCcbStep';
import { FundingStep } from './FundingStep';
import { OriginatorStep } from './OriginatorStep';
import { ProductStep } from './ProductStep';
import { QRCodeStep } from './QRCodeStep';
import { ReviewStep } from './ReviewStep';
import { SimulationV2Step } from './SimulationV2Step';
import { SubscribersStep } from './SubscribersStep';
import { VariablesStep } from './VariablesStep';
import ConsignedStep from './ConsignedStep/ConsignedStep';
import SplitPaymentStep from './SplitPaymentStep/SplitPaymentStep';
import { ConsigneeOrganType } from './ConsignedStep/interfaces';
import FgtsStep from './FgtsStep/FgtsStep';
import { ConsultaFgts } from './ConsultaFgts';
import { TProducts } from 'modules/products/context';
import { EBeneficiaryType } from 'modules/products/context/ProductsProvider/products.interfaces';

export const CONSULTA_FGTS_STEP_TITLE = 'Consulta FGTS';
export const SIMULATION_V2_STEP_TITLE = 'Valores da Solicitação';

const getConsultaFgtsStep = () => ({
  title: CONSULTA_FGTS_STEP_TITLE,
  component: <ConsultaFgts />,
});

const insertConsultaFgtsStep = <
  T extends { title: string; component: JSX.Element },
>(
  steps: T[],
  includeFgtsQuery: boolean,
  afterTitle: string,
) => {
  if (!includeFgtsQuery) {
    return steps;
  }

  const insertIndex = steps.findIndex((step) => step.title === afterTitle);

  if (insertIndex === -1) {
    return steps;
  }

  return [
    ...steps.slice(0, insertIndex + 1),
    getConsultaFgtsStep() as T,
    ...steps.slice(insertIndex + 1),
  ];
};

export const getApplicationSteps = (
  showProduct: boolean,
  product?: TProducts | null,
  includeFgtsQuery = false,
) => {
  const consigneeType = product?.organ_type as ConsigneeOrganType | undefined;

  // Fluxo Siape e CT
  const consigneeSteps = insertConsultaFgtsStep(
    [
      {
        title: 'Funding',
        component: <FundingStep />,
      },
      ...(showProduct
        ? [
            {
              title: 'Produto',
              component: <ProductStep />,
            },
          ]
        : []),
      {
        title: 'Consulta de margem',
        component: <ConsignedStep />,
      },
      {
        title: SIMULATION_V2_STEP_TITLE,
        component: <SimulationV2Step />,
      },
      {
        title: 'Tomador',
        component: <BorrowerStep isUnicPerson />,
      },
      ...(product?.beneficiary_type ===
      EBeneficiaryType.EXTERNAL_REGISTERED_ACCOUNT
        ? [
            {
              title: 'Conta Beneficiária',
              component: <BeneficiaryAccountStep />,
            },
          ]
        : []),
      {
        title: 'Split de Pagamento',
        component: <SplitPaymentStep />,
      },
      {
        title: 'Campos adicionais',
        component: <VariablesStep />,
      },
      {
        title: 'Ficou tudo certo?',
        component: <ReviewStep />,
      },
    ],
    includeFgtsQuery,
    'Consulta de margem',
  );

  // Fluxo FGTS
  const fgtsSteps = insertConsultaFgtsStep(
    [
      {
        title: 'Funding',
        component: <FundingStep />,
      },
      ...(showProduct
        ? [
            {
              title: 'Produto',
              component: <ProductStep />,
            },
          ]
        : []),
      {
        title: 'Consulta de saldo',
        component: <FgtsStep />,
      },
      {
        title: SIMULATION_V2_STEP_TITLE,
        component: <SimulationV2Step />,
      },
      {
        title: 'Tomador',
        component: <BorrowerStep isUnicPerson />,
      },
      {
        title: 'Assinantes',
        component: <SubscribersStep />,
      },
      {
        title: 'Campos adicionais',
        component: <VariablesStep />,
      },
      {
        title: 'Ficou tudo certo?',
        component: <ReviewStep />,
      },
    ],
    includeFgtsQuery,
    'Consulta de saldo',
  );

  // Fluxo Default (todos os demais produtos)
  const defaultSteps = [
    {
      title: 'Originador',
      component: <OriginatorStep />,
    },
    {
      title: 'Funding',
      component: <FundingStep />,
    },
    ...(showProduct
      ? [
          {
            title: 'Produto',
            component: <ProductStep />,
          },
        ]
      : []),
    {
      title: 'Tomador',
      component: <BorrowerStep />,
    },
    {
      title: 'Custodiante',
      component: <CustodianStep />,
    },
    {
      title: 'Conta Beneficiária',
      component: <BeneficiaryAccountStep />,
    },
    {
      title: 'Boletos para desembolso',
      component: <BillPaymentStep />,
    },
    {
      title: 'Pix Copia e Cola',
      component: <QRCodeStep />,
    },
    {
      title: SIMULATION_V2_STEP_TITLE,
      component: <SimulationV2Step />,
    },
    {
      title: 'Split de Pagamento',
      component: <SplitPaymentStep />,
    },
    {
      title: 'Template de CCB',
      component: <CustomCcbStep />,
    },
    {
      title: 'Assinantes',
      component: <SubscribersStep />,
    },
    {
      title: 'Campos adicionais',
      component: <VariablesStep />,
    },
    {
      title: 'Ficou tudo certo?',
      component: <ReviewStep />,
    },
  ];

  const steps = {
    [ConsigneeOrganType.CT]: consigneeSteps,
    [ConsigneeOrganType.SIAPE]: consigneeSteps,
    [ConsigneeOrganType.INSS]: consigneeSteps,
    [ConsigneeOrganType.FGTS]: fgtsSteps,
  };

  if (!consigneeType) {
    return defaultSteps;
  }

  return steps[consigneeType] ?? defaultSteps;
};
